"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const joi_1 = __importDefault(require("joi"));
const errors_1 = require("../../../errors");
const ICondition_1 = require("../../models/rates/ICondition");
class default_1 {
    _validDateTimeOperations = {
        validOperations: [
            ICondition_1.ConditionalOperations.greaterThanOrEqual,
            ICondition_1.ConditionalOperations.greaterThan,
            ICondition_1.ConditionalOperations.notEqual,
            ICondition_1.ConditionalOperations.equal,
            ICondition_1.ConditionalOperations.lessThanOrEqual,
            ICondition_1.ConditionalOperations.lessThan,
            ICondition_1.ConditionalOperations.between,
        ],
        valueType: 'number',
        fieldType: 'datetime',
    };
    _validWMSNumericOperations = {
        validOperations: [
            ICondition_1.ConditionalOperations.notEqual,
            ICondition_1.ConditionalOperations.equal,
        ],
        valueType: 'number',
        fieldType: 'list<numbers>',
    };
    _validWMSTextoperations = {
        validOperations: [
            ICondition_1.ConditionalOperations.notEqual,
            ICondition_1.ConditionalOperations.equal,
        ],
        valueType: 'string',
        fieldType: 'list<strings>', // this is going to differ based the WMS field
    };
    /**
     * NOTE
     * - The between operator is a numeric operator but its valueType must be tuple.
     *   When the object is sent for validation its dataType must be "tuple"
     *   and its value must be a stringified ITuple object
     */
    _validNumericOperations = {
        validOperations: [
            ICondition_1.ConditionalOperations.greaterThanOrEqual,
            ICondition_1.ConditionalOperations.greaterThan,
            ICondition_1.ConditionalOperations.notEqual,
            ICondition_1.ConditionalOperations.equal,
            ICondition_1.ConditionalOperations.lessThanOrEqual,
            ICondition_1.ConditionalOperations.lessThan,
            ICondition_1.ConditionalOperations.between, // read the note above
        ],
        valueType: 'number',
        fieldType: 'number',
    };
    _validTextOperations = {
        validOperations: [
            ICondition_1.ConditionalOperations.beginsWith,
            ICondition_1.ConditionalOperations.endsWith,
            ICondition_1.ConditionalOperations.notEqual,
            ICondition_1.ConditionalOperations.equal,
            ICondition_1.ConditionalOperations.contains,
            ICondition_1.ConditionalOperations.doesNotContain,
        ],
        valueType: 'string',
        fieldType: 'string',
    };
    _validCarrierOperations = {
        validOperations: [
            ICondition_1.ConditionalOperations.notEqual,
            ICondition_1.ConditionalOperations.equal,
            ICondition_1.ConditionalOperations.beginsWith,
            ICondition_1.ConditionalOperations.endsWith,
            ICondition_1.ConditionalOperations.contains,
        ],
        valueType: 'string',
        fieldType: 'carrier',
    };
    _logicOperatorSchema = joi_1.default.object({
        opType: joi_1.default.string().valid(ICondition_1.DataOperatorType.logicOperator).required(),
        operation: joi_1.default.string().required(),
        operands: joi_1.default.array().required(),
    });
    _eventOperandSchema = joi_1.default.object({
        opType: joi_1.default.string().valid(ICondition_1.DataOperatorType.eventOperator).required(),
        conditionalField: joi_1.default.string().required(),
        jsonpath: joi_1.default.string().required(),
    });
    _dateContextSchema = joi_1.default.object({
        type: joi_1.default.string()
            .valid('datetime', 'dayofmonth', 'dayofweek', 'timeofday')
            .required(),
    });
    _carrierContextSchema = joi_1.default.object({
        type: joi_1.default.string().valid('select', 'input', 'combined').required(),
    });
    _valueOperandSchema = joi_1.default.object({
        opType: joi_1.default.string().valid(ICondition_1.DataOperatorType.valueOperator).required(),
        dataType: joi_1.default.string()
            .valid('string', 'number', 'boolean', 'tuple')
            .optional(),
        displayType: joi_1.default.string()
            .valid('string', 'number', 'boolean', 'datetime', 'list<strings>', 'list<numbers>', 'carrier')
            .optional(),
        value: joi_1.default.string().required(),
        context: joi_1.default.object().optional(),
    });
    _tupleSchema = joi_1.default.object({
        from: joi_1.default.number().min(0).required(),
        to: joi_1.default.number().min(0).greater(joi_1.default.ref('from')).required(),
    });
    _joiValidateConfig = { abortEarly: false, convert: false };
    validate(condition, jsonPath = '$') {
        return this.validateCondition(condition, jsonPath);
    }
    validateCondition(condition, jsonPath, isPrevNotOp = false) {
        const validationIssues = [];
        if (!condition) {
            return [
                new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.requiredValue, undefined, undefined, jsonPath),
            ];
        }
        const schemaResults = this._logicOperatorSchema.validate(condition, this._joiValidateConfig);
        validationIssues.push(...this.schemaErrorHandler(schemaResults, jsonPath));
        if (validationIssues.length > 0)
            return validationIssues;
        switch (condition.operation) {
            case ICondition_1.ConditionalOperations.noop: {
                if (condition.operands.length > 0) {
                    return [
                        new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.notAllowed, 'operands', undefined, jsonPath),
                    ];
                }
                break;
            }
            case ICondition_1.ConditionalOperations.and:
            case ICondition_1.ConditionalOperations.or:
            case ICondition_1.ConditionalOperations.xor: {
                for (const operandIndex in condition.operands) {
                    const operand = condition.operands[operandIndex];
                    const operandValidationResult = this.validateCondition(operand, `${jsonPath}.operands[${operandIndex}]`);
                    validationIssues.push(...(operandValidationResult ?? []));
                }
                break;
            }
            case ICondition_1.ConditionalOperations.not: {
                if (isPrevNotOp) {
                    return [
                        new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.notAllowed, 'operation', condition.operation, jsonPath),
                    ];
                }
                const [operand1, operand2] = condition.operands;
                if (operand2) {
                    return [
                        new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.notAllowed, 'operands', undefined, jsonPath),
                    ];
                }
                const operand1Result = this.validateCondition(operand1, `${jsonPath}.operands[0]`, true);
                validationIssues.push(...(operand1Result ?? []));
                break;
            }
            case ICondition_1.ConditionalOperations.greaterThanOrEqual:
            case ICondition_1.ConditionalOperations.lessThanOrEqual:
            case ICondition_1.ConditionalOperations.greaterThan:
            case ICondition_1.ConditionalOperations.lessThan:
            case ICondition_1.ConditionalOperations.equal:
            case ICondition_1.ConditionalOperations.notEqual:
            case ICondition_1.ConditionalOperations.contains:
            case ICondition_1.ConditionalOperations.beginsWith:
            case ICondition_1.ConditionalOperations.endsWith:
            case ICondition_1.ConditionalOperations.between:
            case ICondition_1.ConditionalOperations.doesNotContain: {
                const validationResult = this.validateAtomicCondition(condition, jsonPath);
                validationIssues.push(...(validationResult ?? []));
                break;
            }
            default:
                return [
                    new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'operation', condition.operation, jsonPath),
                ];
        }
        return validationIssues.length > 0 ? validationIssues : undefined;
    }
    validateAtomicCondition(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _condition, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _jsonPath) {
        return undefined;
    }
    validateBetweenOpValue(valueOperand, jsonPath) {
        const validationIssues = [];
        const { value, dataType, displayType } = valueOperand;
        const validDisplayTypes = ['number', 'datetime'];
        if (displayType && !validDisplayTypes.includes(displayType)) {
            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'displayType', valueOperand.displayType, jsonPath));
        }
        if (dataType && dataType !== 'tuple') {
            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'dataType', valueOperand.dataType, jsonPath));
        }
        try {
            const parsedValue = JSON.parse(value);
            const { error } = this._tupleSchema.validate(parsedValue, this._joiValidateConfig);
            if (error) {
                validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'value', valueOperand.value, jsonPath));
            }
        }
        catch {
            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'value', valueOperand.value, jsonPath));
        }
        return validationIssues;
    }
    validateComparison(condition, jsonPath, validConditionalOp, valueOpType) {
        const validationIssues = [];
        if (!validConditionalOp.includes(condition.operation)) {
            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'operation', condition.operation, jsonPath));
        }
        const eventOperand = condition.operands[0];
        const valueOperand = condition.operands[1];
        if (eventOperand) {
            const eventValidationResult = this._eventOperandSchema.validate(eventOperand, this._joiValidateConfig);
            validationIssues.push(...this.schemaErrorHandler(eventValidationResult, `${jsonPath}.operands[0]`));
        }
        if (!valueOperand) {
            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.requiredValue, 'operands', undefined, `${jsonPath}.operands[1]`));
        }
        else {
            const valueOpValidationResult = this._valueOperandSchema.validate(valueOperand, this._joiValidateConfig);
            validationIssues.push(...this.schemaErrorHandler(valueOpValidationResult, `${jsonPath}.operands[1]`));
            if (condition.operation == ICondition_1.ConditionalOperations.between) {
                const betweenValidationResult = this.validateBetweenOpValue(valueOperand, `${jsonPath}.operands[1]`);
                validationIssues.push(...betweenValidationResult);
                return validationIssues;
            }
            if (valueOperand.dataType && valueOperand.dataType != valueOpType) {
                validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'dataType', valueOperand.dataType, `${jsonPath}.operands[1]`));
            }
            if (valueOpType == 'number') {
                const { error } = joi_1.default.number().validate(valueOperand.value);
                if (error) {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidType, 'value', valueOperand.value, `${jsonPath}.operands[1]`));
                }
            }
            else if (valueOpType == 'boolean') {
                const { error } = joi_1.default.boolean().validate(valueOperand.value);
                if (error) {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidType, 'value', valueOperand.value, `${jsonPath}.operands[1]`));
                }
            }
            if (valueOperand.displayType) {
                switch (valueOperand.displayType) {
                    case 'datetime': {
                        if (valueOperand.context) {
                            const { error } = this._dateContextSchema.validate(valueOperand.context);
                            if (error) {
                                validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidType, 'context', valueOperand.context, `${jsonPath}.operands[1]`));
                            }
                        }
                        if (valueOperand.dataType !== 'number') {
                            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'dataType', valueOperand.dataType, `${jsonPath}.operands[1]`));
                        }
                        break;
                    }
                    case 'list<strings>': {
                        if (valueOperand.dataType !== 'string') {
                            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'dataType', valueOperand.dataType, `${jsonPath}.operands[1]`));
                        }
                        break;
                    }
                    case 'list<numbers>': {
                        if (valueOperand.dataType !== 'number') {
                            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'dataType', valueOperand.dataType, `${jsonPath}.operands[1]`));
                        }
                        break;
                    }
                    case 'carrier': {
                        if (valueOperand.context) {
                            const { error } = this._carrierContextSchema.validate(valueOperand.context);
                            if (error) {
                                validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidType, 'context', valueOperand.context, `${jsonPath}.operands[1]`));
                            }
                        }
                        if (valueOperand.dataType !== 'string') {
                            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'dataType', valueOperand.dataType, `${jsonPath}.operands[1]`));
                        }
                        break;
                    }
                    default: {
                        if (valueOperand.displayType !== valueOpType) {
                            validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, 'displayType', valueOperand.displayType, `${jsonPath}.operands[1]`));
                        }
                        break;
                    }
                }
            }
        }
        return validationIssues;
    }
    schemaErrorHandler(result, jsonPath) {
        const validationIssues = [];
        result.error?.details.forEach((issue) => {
            switch (issue.type) {
                case 'any.required': {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.requiredValue, issue.context?.key, undefined, jsonPath));
                    break;
                }
                case 'any.only': {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidValue, issue.context?.key, issue.context?.value, jsonPath));
                    break;
                }
                case 'string.base':
                case 'number.base':
                case 'boolean.base':
                case 'object.base':
                case 'array.base': {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.invalidType, issue.context?.key, issue.context?.value, jsonPath));
                    break;
                }
                case 'object.unknown': {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('ICondition', errors_1.Errors.ValidationErrorCode.notAllowed, issue.context?.key, issue.context?.value, jsonPath));
                    break;
                }
            }
        });
        return validationIssues;
    }
}
exports.default = default_1;
